// src/pages/UserAdmin/TemplateBuilder.js — formulário de template da Meta.
//
// O MESMO componente serve criar e editar (prop `mode`). Não é economia de
// código: é o que impede os dois caminhos de divergirem. Antes deste PR o
// dialog de edição tinha campos próprios e mandava só body/footer/imagem —
// e como a Meta SUBSTITUI o conjunto inteiro de componentes a cada edição,
// qualquer coisa fora desses três era apagada em silêncio. Um formulário só,
// uma validação só, nada some.
//
// Divisão de responsabilidade:
//   - utils/templateMeta.js  → regras puras (variáveis, validação, conversão)
//   - TemplatePreview.js     → a bolha do WhatsApp
//   - este arquivo           → estado e controles
//   - backend templates.py   → AUTORIDADE. É ele que monta o payload da Meta.
//
// Decisão de UX que vale registrar: a lista de variáveis NÃO fica no estado.
// Ela é derivada do texto a cada render. Guardá-la criaria uma segunda fonte
// de verdade que diverge na primeira vez que alguém apaga um {{2}} do meio da
// frase. O que fica no estado é o EXEMPLO de cada variável, num mapa chaveado
// pelo número — num array, apagar o {{1}} deslocaria o exemplo do {{2}} pro
// lugar errado sem ninguém perceber.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Collapse, Divider,
  IconButton, MenuItem, Stack, TextField, ToggleButton, ToggleButtonGroup,
  Tooltip, Typography,
} from '@mui/material';
import {
  FiClipboard, FiCode, FiExternalLink,
  FiImage, FiPhone, FiPlus, FiSend, FiTrash2, FiX, FiBold, FiItalic,
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { MdStrikethroughS } from 'react-icons/md';
import TemplatePreview from './TemplatePreview';
import VariableSourcePicker, { COMPONENT_LABELS, defaultEntry }
  from './VariableSourcePicker';
import api from '../../services/Api';
import {
  LIMITS, extractVars, insertVariableAt,
  renumberVars, toApiPayload, validateTemplate, varsAreSequential,
} from '../../utils/templateMeta';
import {
  formatarBytes, mensagemDeFalhaNoUpload, motivoParaRecusar, prepararImagem,
} from '../../utils/imagemTemplate';
import { compilarPolitica, mensagemTema, verificarTema } from '../../utils/politicaMeta';

const SYNE = "'Syne', system-ui, sans-serif";
const GREEN = '#25d366';

let seq = 0;
const nextId = () => `b${(seq += 1)}`;

// ─── Blocos de apresentação ───────────────────────────────────────────────

function Section({ title, hint, required, children, action }) {
  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
        <Box>
          <Typography variant="caption" sx={{ color: '#475569', fontWeight: 700, letterSpacing: 0.3 }}>
            {title}
            {required && <span style={{ color: '#ef4444' }}> *</span>}
          </Typography>
          {hint && (
            <Typography variant="caption" sx={{ color: '#94a3b8', display: 'block', lineHeight: 1.4 }}>
              {hint}
            </Typography>
          )}
        </Box>
        {action}
      </Stack>
      {children}
    </Box>
  );
}

function FieldErrors({ errors, field }) {
  const mine = errors.filter((e) => e.field === field);
  if (!mine.length) return null;
  return (
    <Stack spacing={0.3} sx={{ mt: 0.75 }}>
      {mine.map((e, i) => (
        <Typography key={i} variant="caption" sx={{ color: '#dc2626', display: 'block' }}>
          {e.message}
        </Typography>
      ))}
    </Stack>
  );
}

function Counter({ value, max }) {
  const length = (value || '').length;
  return (
    <Typography
      variant="caption"
      sx={{ color: length > max ? '#dc2626' : '#94a3b8', fontVariantNumeric: 'tabular-nums' }}
    >
      {length}/{max}
    </Typography>
  );
}

// ─── Botão de formulário (Formulários PR 3, out/26) ──────────────────────
// O template leva UM botão que abre um formulário publicado do mesmo WABA.
// A lista vem da mesma rota do chat (formulários publicados que o chip pode
// mandar); quem confere de verdade é o servidor, na criação.

function FormularioEditor({ form, patch }) {
  const navigate = useNavigate();
  const [lista, setLista] = useState(null);
  const botao = form.flowButton || { flowId: '', text: '' };
  const deviceId = form.deviceId;

  useEffect(() => {
    if (!deviceId) { setLista([]); return undefined; }
    let vivo = true;
    setLista(null);
    api.get('/formularios/para-enviar', { params: { device_id: deviceId } })
      .then((r) => { if (vivo) setLista(r.data?.items || []); })
      .catch(() => { if (vivo) setLista([]); });
    return () => { vivo = false; };
  }, [deviceId]);

  const mudar = (changes) => patch({ flowButton: { ...botao, ...changes } });
  const escolhido = (lista || []).find((f) => f.id === botao.flowId);
  const sumiu = lista && botao.flowId && !escolhido;

  if (lista === null) {
    return <Box sx={{ py: 1 }}><CircularProgress size={18} sx={{ color: GREEN }} /></Box>;
  }
  if (!lista.length && !botao.flowId) {
    return (
      <Alert
        severity="info"
        sx={{ borderRadius: 2, fontSize: '0.82rem' }}
        action={<Button size="small" onClick={() => navigate('/formularios')} sx={{ textTransform: 'none', fontWeight: 700 }}>Abrir Formulários</Button>}
      >
        Nenhum formulário publicado neste chip. Monte e publique um em Gerenciamento → Formulários.
      </Alert>
    );
  }
  return (
    <Stack spacing={1.5}>
      <TextField
        select size="small" fullWidth label="Formulário que o botão abre"
        value={escolhido ? botao.flowId : ''}
        onChange={(e) => {
          const f = lista.find((x) => x.id === e.target.value);
          mudar({ flowId: e.target.value, flowName: f?.name || '' });
        }}
        helperText={escolhido ? (escolhido.roteiro || []).join(' → ') : 'Só aparecem os formulários publicados deste chip.'}
      >
        {lista.map((f) => <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>)}
      </TextField>
      {sumiu && (
        <Alert severity="warning" sx={{ borderRadius: 2, fontSize: '0.82rem' }}>
          O formulário {botao.flowName ? `“${botao.flowName}” ` : ''}deste botão não está mais publicado. Escolha outro.
        </Alert>
      )}
      <TextField
        size="small" fullWidth label="Texto do botão" value={botao.text || ''}
        onChange={(e) => mudar({ text: e.target.value })}
        inputProps={{ maxLength: LIMITS.BUTTON_TEXT }}
        helperText={`${(botao.text || '').length}/${LIMITS.BUTTON_TEXT}`}
      />
      <Typography variant="caption" sx={{ color: '#64748b', lineHeight: 1.5 }}>
        O botão de formulário vai sozinho no template. Pedir dados do cliente costuma ser Marketing;
        a Meta pode reclassificar a categoria na aprovação. Quem tocar responde dentro do WhatsApp,
        e a resposta vai para a conversa, o CRM e a pasta do formulário.
      </Typography>
    </Stack>
  );
}

// ─── Editor de botões ─────────────────────────────────────────────────────

function ButtonsEditor({ form, patch, errors, locked }) {
  const setMode = (_e, mode) => {
    if (mode === null) return;
    // Trocar de modo zera a lista do modo anterior: os modos (respostas,
    // ações e, desde out/26, formulário) são exclusivos, e deixar dados
    // escondidos do outro modo faria o preview e o payload discordarem do
    // que está na tela. O botão de formulário fica guardado: ele só vai pro
    // payload no modo Formulário (templateMeta.formButtonsToApi).
    patch({ buttonMode: mode, quickReplies: [], ctas: [] });
  };

  if (locked) {
    return (
      <Alert severity="warning" sx={{ borderRadius: 2, fontSize: '0.82rem' }}>
        Este template tem botões de um tipo que esta tela ainda não edita (como
        copiar código ou Flow). Os botões foram preservados e o restante do
        template continua editável aqui.
      </Alert>
    );
  }

  const quickReplies = form.quickReplies || [];
  const ctas = form.ctas || [];

  const updateQuick = (id, text) =>
    patch({ quickReplies: quickReplies.map((b) => (b.id === id ? { ...b, text } : b)) });
  const updateCta = (id, changes) =>
    patch({ ctas: ctas.map((c) => (c.id === id ? { ...c, ...changes } : c)) });

  return (
    <Stack spacing={2}>
      <ToggleButtonGroup value={form.buttonMode} exclusive onChange={setMode} size="small">
        <ToggleButton value="NONE" sx={{ textTransform: 'none', px: 2 }}>Nenhum</ToggleButton>
        <ToggleButton value="QUICK_REPLY" sx={{ textTransform: 'none', px: 2 }}>
          Respostas rápidas
        </ToggleButton>
        <ToggleButton value="CTA" sx={{ textTransform: 'none', px: 2 }}>
          Ações (link/telefone)
        </ToggleButton>
        <ToggleButton value="FORM" sx={{ textTransform: 'none', px: 2, gap: 0.75 }}>
          <FiClipboard size={13} /> Formulário
        </ToggleButton>
      </ToggleButtonGroup>

      {form.buttonMode === 'FORM' && <FormularioEditor form={form} patch={patch} />}

      {form.buttonMode === 'QUICK_REPLY' && (
        <Stack spacing={1.25}>
          {quickReplies.map((button, index) => (
            <Stack key={button.id} direction="row" spacing={1} alignItems="flex-start">
              <TextField
                size="small"
                fullWidth
                label={`Botão ${index + 1}`}
                value={button.text}
                onChange={(e) => updateQuick(button.id, e.target.value)}
                inputProps={{ maxLength: LIMITS.BUTTON_TEXT }}
                helperText={`${button.text.length}/${LIMITS.BUTTON_TEXT}`}
              />
              <IconButton
                size="small"
                onClick={() => patch({ quickReplies: quickReplies.filter((b) => b.id !== button.id) })}
                sx={{ color: '#ef4444', mt: 0.5 }}
              >
                <FiTrash2 size={15} />
              </IconButton>
            </Stack>
          ))}
          {quickReplies.length < LIMITS.QUICK_REPLIES && (
            <Button
              size="small"
              startIcon={<FiPlus size={14} />}
              onClick={() => patch({ quickReplies: [...quickReplies, { id: nextId(), text: '' }] })}
              sx={{ textTransform: 'none', color: '#16a34a', alignSelf: 'flex-start' }}
            >
              Adicionar resposta ({quickReplies.length}/{LIMITS.QUICK_REPLIES})
            </Button>
          )}
        </Stack>
      )}

      {form.buttonMode === 'CTA' && (
        <Stack spacing={2}>
          {ctas.map((cta, index) => (
            <Box key={cta.id} sx={{ border: '1px solid #e2e8f0', borderRadius: 2, p: 1.75 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.25}>
                <ToggleButtonGroup
                  value={cta.type}
                  exclusive
                  size="small"
                  onChange={(_e, type) => type && updateCta(cta.id, { type })}
                >
                  <ToggleButton value="URL" sx={{ textTransform: 'none', px: 1.5, gap: 0.5 }}>
                    <FiExternalLink size={13} /> Link
                  </ToggleButton>
                  <ToggleButton value="PHONE_NUMBER" sx={{ textTransform: 'none', px: 1.5, gap: 0.5 }}>
                    <FiPhone size={13} /> Telefone
                  </ToggleButton>
                </ToggleButtonGroup>
                <IconButton
                  size="small"
                  onClick={() => patch({ ctas: ctas.filter((c) => c.id !== cta.id) })}
                  sx={{ color: '#ef4444' }}
                >
                  <FiTrash2 size={15} />
                </IconButton>
              </Stack>

              <Stack spacing={1.25}>
                <TextField
                  size="small"
                  label={`Texto do botão ${index + 1}`}
                  value={cta.text}
                  onChange={(e) => updateCta(cta.id, { text: e.target.value })}
                  inputProps={{ maxLength: LIMITS.BUTTON_TEXT }}
                  helperText={`${(cta.text || '').length}/${LIMITS.BUTTON_TEXT}`}
                  fullWidth
                />

                {cta.type === 'URL' ? (
                  <>
                    <TextField
                      size="small"
                      label="Link"
                      placeholder="https://cettosystem.com.br/pedido/{{1}}"
                      value={cta.url}
                      onChange={(e) => updateCta(cta.id, { url: e.target.value })}
                      fullWidth
                      helperText="Para link dinâmico, use {{1}} no FIM do endereço."
                    />
                    {extractVars(cta.url || '').length > 0 && (
                      <TextField
                        size="small"
                        label="Exemplo do link completo"
                        placeholder="https://cettosystem.com.br/pedido/1234"
                        value={cta.urlExample || ''}
                        onChange={(e) => updateCta(cta.id, { urlExample: e.target.value })}
                        fullWidth
                        helperText="A Meta exige um endereço de exemplo já com a variável preenchida."
                      />
                    )}
                  </>
                ) : (
                  <TextField
                    size="small"
                    label="Telefone"
                    placeholder="+5521999999999"
                    value={cta.phone}
                    onChange={(e) => updateCta(cta.id, { phone: e.target.value })}
                    fullWidth
                    helperText="Formato internacional, com código do país."
                  />
                )}
              </Stack>
            </Box>
          ))}

          {ctas.length < LIMITS.CTA && (
            <Button
              size="small"
              startIcon={<FiPlus size={14} />}
              onClick={() =>
                patch({
                  ctas: [
                    ...ctas,
                    { id: nextId(), type: 'URL', text: '', url: '', urlExample: '', phone: '' },
                  ],
                })
              }
              sx={{ textTransform: 'none', color: '#16a34a', alignSelf: 'flex-start' }}
            >
              Adicionar ação ({ctas.length}/{LIMITS.CTA})
            </Button>
          )}
        </Stack>
      )}

      <FieldErrors errors={errors} field="buttons" />
    </Stack>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────

export default function TemplateBuilder({
  mode = 'create',
  form,
  onChange,
  devices = [],
  onSubmit,
  submitting = false,
  error = '',
  onUploadImage,
  imageResolver,
  lockedButtons = false,
}) {
  const bodyRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  // Erro e aviso do anexo de imagem. Separados: um é falha (nada subiu),
  // o outro é informação (subiu, mas o arquivo foi reduzido).
  const [imagemErro, setImagemErro] = useState('');
  const [imagemNota, setImagemNota] = useState('');
  // A URL está no formulário, mas o navegador não conseguiu exibir. Na
  // EDIÇÃO isso é comum: a Meta devolve o `header_handle` apontando pro
  // CDN dela (scontent.whatsapp.net), que não abre de fora. Sem tratar,
  // a tela mostrava o ícone de imagem quebrada e nenhuma explicação.
  const [imagemQuebrada, setImagemQuebrada] = useState(false);
  // Campos personalizados da empresa, pro seletor de origem. Buscados aqui
  // e não recebidos por prop porque as duas telas que montam este
  // componente não têm essa lista — e ela muda por fora, no CRM.
  const [customFields, setCustomFields] = useState([]);

  useEffect(() => {
    // Na edição o seletor de origem nem é desenhado (ver seção 4), então
    // não há por que buscar a lista.
    if (mode === 'edit') return undefined;
    let vivo = true;
    api.get('/custom-fields', {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then((res) => { if (vivo) setCustomFields(res.data || []); })
      // Falha aqui não pode travar a criação do template: sem a lista, a
      // origem "campo personalizado" fica desabilitada e o resto funciona.
      .catch(() => { if (vivo) setCustomFields([]); });
    return () => { vivo = false; };
  }, [mode]);

  // Temas que a Cetto não envia (out/26, blindagem contra banimento). A
  // lista vem do servidor com o que o ADMIN liberou para a empresa; sem
  // ela (falha de rede), a tela não avisa e o servidor barra no envio.
  const [politica, setPolitica] = useState(null);
  useEffect(() => {
    let vivo = true;
    api.get('/templates/politica', {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    })
      .then((res) => { if (vivo) setPolitica(compilarPolitica(res.data)); })
      .catch(() => { if (vivo) setPolitica(null); });
    return () => { vivo = false; };
  }, []);

  // Toda troca de imagem recomeça a avaliação de "carrega ou não".
  const urlImagem = form.header?.imageUrl || '';
  useEffect(() => { setImagemQuebrada(false); }, [urlImagem]);

  const isEdit = mode === 'edit';
  const patch = useCallback((changes) => onChange({ ...form, ...changes }), [form, onChange]);

  // Derivados — nunca guardados. Ver o cabeçalho do arquivo.
  const bodyVars = useMemo(() => extractVars(form.body), [form.body]);
  const errors = useMemo(() => validateTemplate({ ...form, mode }), [form, mode]);
  // Os MESMOS textos que o servidor confere (templates._barrar_tema_proibido):
  // nome, cabeçalho, corpo, rodapé e texto dos botões.
  const temaProibido = useMemo(() => {
    if (!politica) return null;
    const p = toApiPayload(form);
    return verificarTema(politica, [
      form.name, p.header_text, p.body, p.footer, ...(p.buttons || []).map((b) => b.text),
    ]);
  }, [politica, form]);
  const canSubmit = errors.length === 0 && !submitting && !temaProibido;

  // Todas as variáveis do template, de qualquer componente. Derivadas do
  // texto — mesma regra do backend (services/template_vars.count_vars): o
  // cabeçalho aceita uma, o botão de URL aceita uma no fim, o corpo aceita
  // várias. Cada `slot` carrega como ler e escrever o próprio exemplo, pra
  // o JSX não precisar saber onde cada tipo guarda o dele.
  const headerVars = useMemo(
    () => (form.header?.type === 'TEXT' ? extractVars(form.header.text) : []),
    [form.header?.type, form.header?.text]
  );
  const urlVarCta = useMemo(
    () => (form.buttonMode === 'CTA'
      ? (form.ctas || []).find((c) => c.type === 'URL' && extractVars(c.url || '').length)
      : null),
    [form.buttonMode, form.ctas]
  );

  const allVarSlots = useMemo(() => {
    const slots = [];
    if (headerVars.length) {
      slots.push({
        key: 'HEADER:1', component: 'HEADER', position: 1,
        example: form.headerExample || '',
        setExample: (v) => patch({ headerExample: v }),
      });
    }
    [...bodyVars].sort((a, b) => a - b).forEach((n) => {
      slots.push({
        key: `BODY:${n}`, component: 'BODY', position: n,
        example: form.examples?.[n] || '',
        setExample: (v) => patch({ examples: { ...form.examples, [n]: v } }),
      });
    });
    if (urlVarCta) {
      slots.push({
        key: 'BUTTON_URL:1', component: 'BUTTON_URL', position: 1,
        example: urlVarCta.urlExample || '',
        setExample: (v) => patch({
          ctas: (form.ctas || []).map((c) => (c.id === urlVarCta.id ? { ...c, urlExample: v } : c)),
        }),
      });
    }
    return slots;
  }, [headerVars, bodyVars, urlVarCta, form.headerExample, form.examples, form.ctas, patch]);

  const mappingBySlot = useMemo(() => Object.fromEntries(
    (form.variableMapping || []).map((e) => [`${e.component}:${e.position}`, e])
  ), [form.variableMapping]);

  // Só as origens das variáveis que EXISTEM hoje. Quem apagou um {{3}} do
  // texto não deve continuar mandando a origem dele — o backend recusaria
  // por não bater com a contagem.
  const cleanMapping = useMemo(
    () => allVarSlots.map((slot) => (
      mappingBySlot[slot.key] || defaultEntry(slot.component, slot.position)
    )),
    [allVarSlots, mappingBySlot]
  );

  const setMappingEntry = useCallback((key, next) => {
    patch({
      variableMapping: [
        ...(form.variableMapping || []).filter((e) => `${e.component}:${e.position}` !== key),
        next,
      ],
    });
  }, [form.variableMapping, patch]);

  const wrapSelection = (left, right = left) => {
    const input = bodyRef.current;
    if (!input) return;
    const { selectionStart: start, selectionEnd: end } = input;
    const selected = form.body.slice(start, end) || 'texto';
    patch({ body: `${form.body.slice(0, start)}${left}${selected}${right}${form.body.slice(end)}` });
    setTimeout(() => {
      input.focus();
      input.setSelectionRange(start + left.length, start + left.length + selected.length);
    }, 0);
  };

  const addVariable = () => {
    const input = bodyRef.current;
    const start = input ? input.selectionStart : form.body.length;
    const end = input ? input.selectionEnd : form.body.length;
    const { text, caret } = insertVariableAt(form.body, start, end);
    patch({ body: text });
    setTimeout(() => {
      input?.focus();
      input?.setSelectionRange(caret, caret);
    }, 0);
  };

  const handleRenumber = () => {
    const { text, examples } = renumberVars(form.body, form.examples);
    patch({ body: text, examples });
  };

  const handleImage = async (e) => {
    const file = e.target.files?.[0];
    // Limpa o input já: escolher o MESMO arquivo de novo (depois de
    // corrigir o formato, por exemplo) precisa disparar o evento outra vez.
    e.target.value = '';
    if (!file || !onUploadImage) return;

    setImagemErro('');
    setImagemNota('');

    // Recusa o que nem adianta subir (HEIC, formato errado, acima do teto
    // da Meta) antes de gastar o upload.
    const recusa = motivoParaRecusar(file);
    if (recusa) {
      setImagemErro(recusa);
      return;
    }

    setUploading(true);
    try {
      const { file: pronto, otimizada, deBytes, paraBytes } = await prepararImagem(file);
      const url = await onUploadImage(pronto);
      patch({ header: { ...form.header, type: 'IMAGE', imageUrl: url } });
      if (otimizada) {
        // Dito em voz alta de propósito: trocar o arquivo da pessoa em
        // silêncio é o tipo de gentileza que vira reclamação depois.
        setImagemNota(
          `Imagem reduzida de ${formatarBytes(deBytes)} para `
          + `${formatarBytes(paraBytes)} para caber no envio.`
        );
      }
    } catch (err) {
      // ESTE catch é o conserto do defeito original: sem ele o erro virava
      // uma rejeição não tratada, o botão voltava ao normal e a tela não
      // dizia nada — a pessoa escolhia a foto e "não acontecia nada".
      setImagemErro(mensagemDeFalhaNoUpload(err));
    } finally {
      setUploading(false);
    }
  };

  const setHeaderType = (_e, type) => {
    if (type === null) return;
    patch({
      header: { ...form.header, type },
      // Exemplo do cabeçalho só faz sentido no modo texto.
      headerExample: type === 'TEXT' ? form.headerExample : '',
    });
  };

  return (
    <Stack
      direction={{ xs: 'column', lg: 'row' }}
      spacing={3}
      alignItems="flex-start"
      sx={{ p: { xs: 2.5, md: 4 } }}
    >
      {/* ─── Coluna do formulário ─── */}
      <Stack spacing={3} sx={{ flex: 1, minWidth: 0, width: '100%' }}>
        {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}

        {/* 1. Identificação */}
        <Section
          title="Identificação"
          hint={isEdit ? 'Nome, categoria e idioma são imutáveis depois do envio à Meta.' : null}
        >
          <Stack spacing={2}>
            {/* `minWidth: 0` nas duas colunas: sem isso o nome do chip
                ("Comercial Atendimento Principal — 5521999990000") vira a
                largura mínima da linha e empurra o formulário pra fora da
                tela. Item de flex não encolhe abaixo do conteúdo por
                padrão. */}
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <TextField
                  label="Nome do template"
                  value={form.name}
                  onChange={(e) =>
                    patch({ name: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })
                  }
                  disabled={isEdit}
                  fullWidth
                  size="small"
                  helperText="Minúsculas, números e _ (ex: promocao_inverno_2026)"
                />
                <FieldErrors errors={errors} field="name" />
              </Box>
              {!isEdit && (
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <TextField
                    select
                    label="Chip (WABA)"
                    value={form.deviceId}
                    onChange={(e) => patch({ deviceId: e.target.value })}
                    fullWidth
                    size="small"
                    helperText="O template será criado no WABA deste chip"
                  >
                    {devices.map((d) => (
                      <MenuItem key={d.id} value={d.id}>
                        {d.name} — {d.phone_number}
                      </MenuItem>
                    ))}
                  </TextField>
                  <FieldErrors errors={errors} field="deviceId" />
                </Box>
              )}
            </Stack>

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                select
                label="Categoria"
                value={form.category}
                onChange={(e) => patch({ category: e.target.value })}
                disabled={isEdit}
                fullWidth
                size="small"
              >
                <MenuItem value="MARKETING">Marketing</MenuItem>
                <MenuItem value="UTILITY">Utilidade (notificações)</MenuItem>
              </TextField>
              <TextField
                select
                label="Idioma"
                value={form.language}
                onChange={(e) => patch({ language: e.target.value })}
                disabled={isEdit}
                fullWidth
                size="small"
              >
                <MenuItem value="pt_BR">Português (Brasil)</MenuItem>
                <MenuItem value="pt_PT">Português (Portugal)</MenuItem>
                <MenuItem value="en_US">Inglês (EUA)</MenuItem>
                <MenuItem value="es_ES">Espanhol</MenuItem>
              </TextField>
            </Stack>
          </Stack>
        </Section>

        <Divider />

        {/* 2. Cabeçalho */}
        <Section title="Cabeçalho" hint="Opcional. Aparece em destaque acima da mensagem.">
          <Stack spacing={1.75}>
            <ToggleButtonGroup value={form.header?.type || 'NONE'} exclusive onChange={setHeaderType} size="small">
              <ToggleButton value="NONE" sx={{ textTransform: 'none', px: 2 }}>Nenhum</ToggleButton>
              <ToggleButton value="TEXT" sx={{ textTransform: 'none', px: 2 }}>Texto</ToggleButton>
              <ToggleButton value="IMAGE" sx={{ textTransform: 'none', px: 2 }}>Imagem</ToggleButton>
            </ToggleButtonGroup>

            {form.header?.type === 'TEXT' && (
              <>
                <TextField
                  size="small"
                  fullWidth
                  label="Texto do cabeçalho"
                  placeholder="Oferta exclusiva para {{1}}"
                  value={form.header.text}
                  onChange={(e) => patch({ header: { ...form.header, text: e.target.value } })}
                  inputProps={{ maxLength: LIMITS.HEADER_TEXT }}
                  helperText={`${(form.header.text || '').length}/${LIMITS.HEADER_TEXT} — aceita no máximo uma variável`}
                />
                {extractVars(form.header.text || '').length > 0 && (
                  <TextField
                    size="small"
                    fullWidth
                    label="Exemplo da variável do cabeçalho"
                    placeholder="João"
                    value={form.headerExample || ''}
                    onChange={(e) => patch({ headerExample: e.target.value })}
                  />
                )}
              </>
            )}

            {form.header?.type === 'IMAGE' && (
              form.header.imageUrl ? (
                <Stack
                  direction="row"
                  spacing={2}
                  alignItems="center"
                  sx={{ border: '1px solid #e2e8f0', borderRadius: 2, p: 1.5, bgcolor: '#f8fafc' }}
                >
                  {imagemQuebrada ? (
                    <Box sx={{
                      width: 56, height: 56, borderRadius: 1, flexShrink: 0,
                      bgcolor: '#f1f5f9', color: '#94a3b8',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <FiImage size={20} />
                    </Box>
                  ) : (
                    <Box
                      component="img"
                      src={imageResolver ? imageResolver(form.header.imageUrl) : form.header.imageUrl}
                      alt=""
                      onError={() => setImagemQuebrada(true)}
                      sx={{ width: 56, height: 56, borderRadius: 1, objectFit: 'cover', flexShrink: 0 }}
                    />
                  )}
                  <Typography
                    variant="caption"
                    sx={{
                      flex: 1, minWidth: 0, fontWeight: 700,
                      color: imagemQuebrada ? '#b45309' : '#16a34a',
                    }}
                  >
                    {imagemQuebrada
                      ? 'Imagem anexada (sem prévia disponível)'
                      : '✓ Imagem carregada'}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => patch({ header: { ...form.header, imageUrl: '' } })}
                    sx={{ color: '#ef4444' }}
                  >
                    <FiX size={16} />
                  </IconButton>
                </Stack>
              ) : (
                <Button
                  variant="outlined"
                  component="label"
                  disabled={uploading}
                  startIcon={uploading ? <CircularProgress size={14} /> : <FiImage size={16} />}
                  sx={{
                    textTransform: 'none', borderColor: '#e2e8f0', color: '#64748b',
                    alignSelf: 'flex-start',
                    '&:hover': { borderColor: GREEN, color: '#16a34a', bgcolor: '#f0fdf4' },
                  }}
                >
                  {uploading ? 'Enviando…' : 'Enviar imagem (JPG ou PNG, até 5 MB)'}
                  <input type="file" hidden accept="image/jpeg,image/png" onChange={handleImage} />
                </Button>
              )
            )}

            {/* Falha do anexo. Antes disto o erro virava rejeição não
                tratada e a tela ficava muda — a pessoa escolhia a foto e
                achava que o clique não tinha pegado. */}
            {form.header?.type === 'IMAGE' && imagemErro && (
              <Alert
                severity="error" onClose={() => setImagemErro('')}
                sx={{ borderRadius: 2, fontSize: '0.82rem' }}
              >
                {imagemErro}
              </Alert>
            )}
            {form.header?.type === 'IMAGE' && imagemNota && (
              <Alert
                severity="info" onClose={() => setImagemNota('')}
                sx={{ borderRadius: 2, fontSize: '0.82rem' }}
              >
                {imagemNota}
              </Alert>
            )}

            <FieldErrors errors={errors} field="header" />
          </Stack>
        </Section>

        <Divider />

        {/* 3. Corpo */}
        <Section
          title="Mensagem"
          required
          action={
            <Stack direction="row" spacing={0.5} alignItems="center">
              <Tooltip title="Negrito"><IconButton size="small" onClick={() => wrapSelection('*')}><FiBold size={14} /></IconButton></Tooltip>
              <Tooltip title="Itálico"><IconButton size="small" onClick={() => wrapSelection('_')}><FiItalic size={14} /></IconButton></Tooltip>
              <Tooltip title="Tachado"><IconButton size="small" onClick={() => wrapSelection('~')}><MdStrikethroughS size={16} /></IconButton></Tooltip>
              <Tooltip title="Monospace"><IconButton size="small" onClick={() => wrapSelection('```')}><FiCode size={14} /></IconButton></Tooltip>
              <Button
                size="small"
                onClick={addVariable}
                sx={{
                  textTransform: 'none', fontWeight: 700, color: '#16a34a',
                  bgcolor: '#f0fdf4', ml: 0.5, px: 1.25,
                  '&:hover': { bgcolor: '#dcfce7' },
                }}
              >
                {'{ }'} Adicionar variável
              </Button>
            </Stack>
          }
        >
          <TextField
            inputRef={bodyRef}
            value={form.body}
            onChange={(e) => patch({ body: e.target.value })}
            placeholder="Olá {{1}}, seu pedido {{2}} saiu para entrega."
            multiline
            minRows={5}
            fullWidth
          />
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
            <Typography variant="caption" sx={{ color: '#94a3b8' }}>
              Selecione um trecho e use os botões acima para formatar.
            </Typography>
            <Counter value={form.body} max={LIMITS.BODY} />
          </Stack>
          <FieldErrors errors={errors} field="body" />
        </Section>

        {/* 4. Variáveis — aparecem sozinhas quando há {{n}} no template.
            Cada uma tem DUAS coisas que parecem parecidas e não são:
              · o EXEMPLO, que a Meta usa pra revisar e aprovar o template;
              · a ORIGEM, que o sistema usa pra preencher no envio.
            Ficam lado a lado porque pertencem à mesma variável, e a legenda
            de cada campo diz pra que serve — pedir isso em duas telas
            diferentes fazia a pessoa criar o template e esquecer a origem.

            A ORIGEM só aparece na criação. Na edição ela pertence ao template
            que já existe na Meta e é alterada pelo diálogo "Variáveis" da
            outra aba, que valida contra o que está lá; desenhar o seletor
            aqui seria oferecer um campo que o envio descarta em silêncio. */}
        <Collapse in={allVarSlots.length > 0} unmountOnExit>
          <Section
            title="Variáveis"
            hint={isEdit
              ? 'Obrigatório: a Meta usa estes exemplos para revisar o template.'
              : 'O exemplo é pra Meta aprovar. A origem é de onde o valor sai no envio.'}
            action={
              !varsAreSequential(bodyVars) && (
                <Button
                  size="small"
                  onClick={handleRenumber}
                  sx={{ textTransform: 'none', color: '#b45309', fontWeight: 700 }}
                >
                  Renumerar variáveis
                </Button>
              )
            }
          >
            <Stack spacing={2}>
              {allVarSlots.map((slot) => (
                <Box
                  key={slot.key}
                  sx={{ border: '1px solid #e2e8f0', borderRadius: 2, p: 1.75 }}
                >
                  <Stack direction="row" alignItems="center" spacing={1} mb={1.5}>
                    <Chip
                      label={`{{${slot.position}}}`}
                      size="small"
                      sx={{
                        fontFamily: 'monospace', fontWeight: 700,
                        bgcolor: '#fef3c7', color: '#92400e',
                      }}
                    />
                    <Typography variant="caption" sx={{ color: '#64748b' }}>
                      no {COMPONENT_LABELS[slot.component] || slot.component}
                    </Typography>
                  </Stack>

                  {/* Quebra por ESPAÇO, não por breakpoint de viewport: o que
                      manda aqui é a largura da coluna do formulário, que
                      encolhe quando a coluna do preview aparece ao lado.
                      Com `md` os dois campos ficavam espremidos em ~215px
                      num notebook de 1280 e o rótulo saía cortado. Com
                      `flex-basis: 230px` + wrap, eles se empilham sozinhos
                      quando não cabem lado a lado. */}
                  <Stack
                    direction="row" useFlexGap flexWrap="wrap"
                    spacing={1.5} alignItems="flex-start"
                  >
                    <Box sx={{ flex: '1 1 230px', minWidth: 0 }}>
                      <TextField
                        size="small"
                        fullWidth
                        label="Exemplo"
                        placeholder={slot.position === 1 ? 'Ex.: João' : 'Ex.: R$ 100,00'}
                        value={slot.example}
                        onChange={(e) => slot.setExample(e.target.value)}
                        helperText="A Meta usa para aprovar"
                      />
                    </Box>
                    {!isEdit && (
                      <Box sx={{ flex: '1 1 230px', minWidth: 0 }}>
                        <VariableSourcePicker
                          compact
                          entry={mappingBySlot[slot.key] || defaultEntry(slot.component, slot.position)}
                          customFields={customFields}
                          onChange={(next) => setMappingEntry(slot.key, next)}
                        />
                      </Box>
                    )}
                  </Stack>
                </Box>
              ))}
              <FieldErrors errors={errors} field="vars" />
            </Stack>
          </Section>
        </Collapse>

        <Divider />

        {/* 5. Rodapé */}
        <Section title="Rodapé" hint="Opcional. Texto pequeno e cinza abaixo da mensagem — não aceita variáveis.">
          <TextField
            size="small"
            fullWidth
            value={form.footer}
            onChange={(e) => patch({ footer: e.target.value })}
            placeholder="Cetto System • Não responda este número"
            inputProps={{ maxLength: LIMITS.FOOTER }}
          />
          <Stack direction="row" justifyContent="flex-end" sx={{ mt: 0.5 }}>
            <Counter value={form.footer} max={LIMITS.FOOTER} />
          </Stack>
          <FieldErrors errors={errors} field="footer" />
        </Section>

        <Divider />

        {/* 6. Botões */}
        <Section
          title="Botões"
          hint={`Opcional. Até ${LIMITS.QUICK_REPLIES} respostas rápidas OU ${LIMITS.CTA} ações OU um formulário.`}
        >
          <ButtonsEditor form={form} patch={patch} errors={errors} locked={lockedButtons} />
        </Section>

        {/* Onde a origem é decidida muda conforme o modo, então o aviso
            também muda. Na CRIAÇÃO ela já foi respondida ali em cima, e o
            que resta dizer é que dá pra mudar depois. Na EDIÇÃO o formulário
            não mostra as origens: elas pertencem ao template que já existe
            na Meta e são alteradas pelo diálogo "Variáveis" da outra aba —
            mandar daqui sobrescreveria o mapeamento com o que esta tela
            acha que são as variáveis. */}
        {allVarSlots.length > 0 && (
          <Alert severity="info" sx={{ borderRadius: 2, fontSize: '0.82rem' }}>
            {isEdit ? (
              <>
                Para mudar de onde sai o valor de cada variável, use o botão
                <strong> Variáveis</strong> no card do template, aqui mesmo na
                aba <strong>Ver e editar templates</strong>.
              </>
            ) : (
              <>
                A origem que você escolheu acima fica salva junto do template
                e pode ser alterada depois em <strong>Ver e editar
                templates</strong> → <strong>Variáveis</strong>. Onde a origem
                for <strong>Perguntar no envio</strong>, o valor é digitado a
                cada disparo.
              </>
            )}
          </Alert>
        )}

        {temaProibido && (
          <Alert severity="error" sx={{ borderRadius: 2, fontSize: '0.82rem' }}>
            {mensagemTema(temaProibido)}
          </Alert>
        )}

        <Button
          variant="contained"
          onClick={() => onSubmit({
            ...toApiPayload(form),
            // Na edição o mapeamento é alterado pelo diálogo próprio, que
            // valida contra o template que existe na Meta. Mandar daqui
            // sobrescreveria com o que a tela acha que são as variáveis.
            variable_mapping: isEdit ? undefined : cleanMapping,
          })}
          disabled={!canSubmit}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <FiSend size={16} />}
          sx={{
            bgcolor: GREEN, color: '#0f172a', fontWeight: 700, fontFamily: SYNE,
            textTransform: 'none', py: 1.3, borderRadius: 2,
            boxShadow: '0 4px 15px rgba(37,211,102,0.3)',
            '&:hover': { bgcolor: '#16a34a', color: '#fff' },
            '&.Mui-disabled': { bgcolor: '#e2e8f0', color: '#94a3b8', boxShadow: 'none' },
          }}
        >
          {submitting
            ? 'Enviando para a Meta…'
            : isEdit
              ? 'Salvar alterações'
              : 'Enviar para aprovação da Meta'}
        </Button>

        {errors.length > 0 && (
          <Typography variant="caption" sx={{ color: '#94a3b8', textAlign: 'center' }}>
            {errors.length === 1
              ? '1 item precisa de atenção antes de enviar.'
              : `${errors.length} itens precisam de atenção antes de enviar.`}
          </Typography>
        )}
      </Stack>

      {/* ─── Coluna do preview ─── */}
      <Box
        sx={{
          width: { xs: '100%', lg: 380 },
          flexShrink: 0,
          position: { lg: 'sticky' },
          top: { lg: 24 },
        }}
      >
        <Typography variant="caption" sx={{ color: '#475569', fontWeight: 700, display: 'block', mb: 1 }}>
          Como o cliente vai ver
        </Typography>
        <TemplatePreview form={form} imageResolver={imageResolver} />
      </Box>
    </Stack>
  );
}
